/** Hand an appointment over to Google Calendar's own compose screen.
 *
 *  The CRM's booking form covers the mortgage case — a meeting with a person,
 *  attached to their record. It deliberately does not try to be Google: guests
 *  and invitations, a Meet link, recurrence, rooms, notification rules. When an
 *  officer needs those, the quickest honest answer is Google's own interface,
 *  prefilled with whatever they have typed so far.
 *
 *  The catch, and the reason this is an escape hatch rather than the default:
 *  an event created on Google comes back with no CRM link, so it will not show
 *  on anyone's contact card. Booking from a contact's Meetings tab is what
 *  creates that link.
 */

/** Google's compose URL wants UTC basic-format timestamps: 20261009T150000Z. */
function stamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

export function googleComposeUrl(opts: {
  title?: string
  startsAt?: Date | null
  endsAt?: Date | null
  location?: string | null
  details?: string | null
  /** Which signed-in Google account to open it under. */
  account?: string | null
}): string {
  const p = new URLSearchParams()
  if (opts.title) p.set('text', opts.title)
  if (opts.startsAt && opts.endsAt) p.set('dates', `${stamp(opts.startsAt)}/${stamp(opts.endsAt)}`)
  if (opts.location) p.set('location', opts.location)
  if (opts.details) p.set('details', opts.details)
  // Without this, Google opens under whichever account is "first" in the
  // browser — which for most people is their personal one, not the work
  // calendar the CRM is synced with.
  if (opts.account) p.set('authuser', opts.account)
  return `https://calendar.google.com/calendar/r/eventedit?${p.toString()}`
}

/** Open a link in a new tab without handing the opener window over to it. */
export function openExternal(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer')
}
